import { createFileRoute } from '@tanstack/react-router';
import { useState, useRef, useEffect } from 'react';
import { Users, ArrowRight } from 'lucide-react';
import { RankIcon, rankColors, Avatar, RatingChart, Button } from '../../components/ui';

type Player = {
  rank: number;
  name: string;
  initials: string;
  elo: number;
  rankTier: 'champion' | 'prime' | 'diamond' | 'platinum' | 'gold' | 'silver' | 'bronze' | 'unranked';
  level: 0 | 1 | 2 | 3 | 4;
  isYou?: boolean;
};

export const Route = createFileRoute('/app/')({
  staticData: { title: 'Competitive Hub' },
  component: CompetitiveHubPage,
});

// Dummy data
const userStats = {
  rank: 'champion' as const,
  rankLevel: 0 as const,
  elo: 3000,
  winRate: 59,
  games: 240,
  streak: 5,
  peakElo: 1923,
  wins: 142,
  losses: 98,
};

const ratingHistory = [
  { date: '03', value: 1420 },
  { date: '05', value: 1520 },
  { date: '07', value: 1570 },
  { date: '09', value: 1620 },
  { date: '11', value: 1720 },
  { date: '13', value: 1800 },
  { date: '15', value: 1850 },
  { date: '17', value: 1880 },
  { date: '19', value: 1947 },
  { date: '21', value: 1900 },
  { date: '23', value: 1947 },
];

const winLossData = [
  { date: '4/3', wins: 0, losses: 1 },
  { date: '4/5', wins: 0, losses: 0 },
  { date: '4/11', wins: 3, losses: 0 },
  { date: '4/12', wins: 6, losses: 2 },
  { date: '4/13', wins: 8, losses: 3 },
  { date: '4/14', wins: 10, losses: 2 },
  { date: '4/15', wins: 12, losses: 4 },
];

const leaderboard = [
  { rank: 1, name: 'ShadowStrike', initials: 'SH', elo: 2540, rankTier: 'champion' as const, level: 0 as const },
  { rank: 2, name: 'NeonPhantom', initials: 'NE', elo: 2380, rankTier: 'prime' as const, level: 0 as const },
  { rank: 3, name: 'VoidWalker', initials: 'VO', elo: 2250, rankTier: 'prime' as const, level: 0 as const },
  { rank: 4, name: 'IceBreaker', initials: 'IC', elo: 2090, rankTier: 'diamond' as const, level: 4 as const },
  { rank: 5, name: 'ThunderBolt', initials: 'TH', elo: 1947, rankTier: 'diamond' as const, level: 3 as const },
  { rank: 6, name: 'PhoenixRise', initials: 'PH', elo: 1820, rankTier: 'platinum' as const, level: 4 as const },
  { rank: 7, name: 'SilentFury', initials: 'SI', elo: 1750, rankTier: 'platinum' as const, level: 3 as const },
  { rank: 8, name: 'BlazeMaster', initials: 'BL', elo: 1680, rankTier: 'gold' as const, level: 4 as const },
  { rank: 9, name: 'NightHawk', initials: 'NI', elo: 1620, rankTier: 'gold' as const, level: 3 as const },
  { rank: 10, name: 'StormBreaker', initials: 'ST', elo: 1580, rankTier: 'gold' as const, level: 2 as const },
  { rank: 11, name: 'CyberWolf', initials: 'CY', elo: 1540, rankTier: 'gold' as const, level: 1 as const },
  { rank: 12, name: 'DarkMatter', initials: 'DA', elo: 1490, rankTier: 'silver' as const, level: 4 as const },
  { rank: 13, name: 'GhostRider', initials: 'GH', elo: 1455, rankTier: 'silver' as const, level: 3 as const },
  { rank: 14, name: 'IronClad', initials: 'IR', elo: 1420, rankTier: 'silver' as const, level: 3 as const },
  { rank: 15, name: 'JetStream', initials: 'JE', elo: 1385, rankTier: 'silver' as const, level: 2 as const },
  { rank: 16, name: 'KnightOwl', initials: 'KN', elo: 1350, rankTier: 'silver' as const, level: 2 as const },
  { rank: 17, name: 'LunarEclipse', initials: 'LU', elo: 1315, rankTier: 'silver' as const, level: 1 as const },
  { rank: 18, name: 'MysticFire', initials: 'MY', elo: 1280, rankTier: 'silver' as const, level: 1 as const },
  { rank: 19, name: 'NovaFlare', initials: 'NO', elo: 1245, rankTier: 'bronze' as const, level: 4 as const },
  { rank: 20, name: 'OmegaForce', initials: 'OM', elo: 1210, rankTier: 'bronze' as const, level: 4 as const },
  { rank: 21, name: 'PrismBlade', initials: 'PR', elo: 1175, rankTier: 'bronze' as const, level: 3 as const },
  { rank: 22, name: 'QuantumLeap', initials: 'QU', elo: 1140, rankTier: 'bronze' as const, level: 3 as const },
  { rank: 23, name: 'RazorEdge', initials: 'RA', elo: 1105, rankTier: 'bronze' as const, level: 2 as const },
  { rank: 24, name: 'SolarFlare', initials: 'SO', elo: 1070, rankTier: 'bronze' as const, level: 2 as const },
  { rank: 25, name: 'ProPlayer123', initials: 'PR', elo: 1035, rankTier: 'bronze' as const, level: 1 as const },
  { rank: 26, name: 'UltraViolet', initials: 'UL', elo: 1000, rankTier: 'bronze' as const, level: 1 as const },
  { rank: 27, name: 'VortexWind', initials: 'VO', elo: 965, rankTier: 'bronze' as const, level: 1 as const },
  { rank: 28, name: 'WarpDrive', initials: 'WA', elo: 930, rankTier: 'bronze' as const, level: 1 as const },
  { rank: 29, name: 'XenonRush', initials: 'XE', elo: 895, rankTier: 'bronze' as const, level: 1 as const },
  { rank: 30, name: 'YellowJacket', initials: 'YE', elo: 860, rankTier: 'bronze' as const, level: 1 as const },
  { rank: 31, name: 'ZeroGravity', initials: 'ZE', elo: 825, rankTier: 'bronze' as const, level: 1 as const },
  { rank: 32, name: 'AlphaStrike', initials: 'AL', elo: 790, rankTier: 'bronze' as const, level: 1 as const },
  { rank: 33, name: 'BetaWave', initials: 'BE', elo: 755, rankTier: 'bronze' as const, level: 1 as const },
  { rank: 34, name: 'CosmicDust', initials: 'CO', elo: 720, rankTier: 'bronze' as const, level: 1 as const },
  { rank: 35, name: 'DeltaForce', initials: 'DE', elo: 685, rankTier: 'bronze' as const, level: 1 as const },
  { rank: 36, name: 'EchoStar', initials: 'EC', elo: 650, rankTier: 'bronze' as const, level: 1 as const },
  { rank: 37, name: 'FluxCapacitor', initials: 'FL', elo: 615, rankTier: 'bronze' as const, level: 1 as const },
  { rank: 38, name: 'GammaRay', initials: 'GA', elo: 580, rankTier: 'bronze' as const, level: 1 as const },
  { rank: 39, name: 'HyperNova', initials: 'HY', elo: 545, rankTier: 'bronze' as const, level: 1 as const },
  { rank: 40, name: 'InfernoBlaze', initials: 'IN', elo: 510, rankTier: 'bronze' as const, level: 1 as const },
  { rank: 41, name: 'JadePhoenix', initials: 'JA', elo: 475, rankTier: 'bronze' as const, level: 1 as const },
  { rank: 42, name: 'KryptonKnight', initials: 'KR', elo: 440, rankTier: 'bronze' as const, level: 1 as const },
  { rank: 43, name: 'LightningBolt', initials: 'LI', elo: 405, rankTier: 'bronze' as const, level: 1 as const },
  { rank: 44, name: 'MeteorShower', initials: 'ME', elo: 370, rankTier: 'bronze' as const, level: 1 as const },
  { rank: 45, name: 'NebulaDrift', initials: 'NE', elo: 335, rankTier: 'bronze' as const, level: 1 as const },
  { rank: 46, name: 'OrionHunter', initials: 'OR', elo: 300, rankTier: 'bronze' as const, level: 1 as const },
  { rank: 47, name: 'PulsarBeam', initials: 'PU', elo: 265, rankTier: 'bronze' as const, level: 1 as const },
  { rank: 48, name: 'QuasarBurst', initials: 'QA', elo: 230, rankTier: 'bronze' as const, level: 1 as const },
  { rank: 49, name: 'RadiantSun', initials: 'RA', elo: 195, rankTier: 'bronze' as const, level: 1 as const },
  { rank: 50, name: 'StarGazer', initials: 'ST', elo: 160, rankTier: 'bronze' as const, level: 1 as const },
];

const currentUser: Player = {
  rank: 25,
  name: 'ProPlayer123',
  initials: 'PR',
  elo: 1035,
  rankTier: 'bronze' as const,
  level: 1 as const,
  isYou: true,
};

function CompetitiveHubPage() {
  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold">Valorant Competitive Hub</h1>
        <p className="text-surface-400 mt-1">Valorant • Competitive • 5v5</p>
        <Button variant="outline" size="sm" className="mt-4" leftIcon={<Users className="size-4" />} rightIcon={<ArrowRight className="size-4" />}>
          View Club
        </Button>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Stats */}
        <div className="space-y-4">
          {/* Main Rank Card */}
          <div className="p-6 rounded-2xl bg-surface-800/50 border border-surface-700/50 relative overflow-hidden group">
            {/* Background effects */}
            <div className={`absolute top-0 right-0 w-64 h-64 ${rankColors[userStats.rank].glow} rounded-full blur-3xl`} />

            <div className="relative flex items-start justify-between mb-3">
              <div className="flex-1 space-y-2">
                <p className={`text-4xl font-black uppercase tracking-tight ${rankColors[userStats.rank].text} drop-shadow-sm`}>
                  Champion
                </p>
                <div className="flex items-center gap-3">
                  <p className="text-6xl font-black drop-shadow-lg">{userStats.elo}</p>
                  <div className="flex flex-col">
                    <p className="text-xs text-surface-400 font-semibold uppercase tracking-wide">ELO</p>
                    <p className="text-[10px] text-surface-500">Rating</p>
                  </div>
                </div>
              </div>

              {/* Rank badge with glow */}
              <div className="relative ml-4 mt-3">
                <div className={`absolute inset-0 ${rankColors[userStats.rank].glowStrong} blur-3xl rounded-full opacity-60 group-hover:opacity-80 transition-opacity duration-300`} />
                <div className={`absolute inset-0 ${rankColors[userStats.rank].glow} blur-xl rounded-full animate-pulse`} />
                <RankIcon rank={userStats.rank} size="xxl" showLevels={false} className="relative" />
              </div>
            </div>

            {/* Stats Row */}
            <div className="relative flex items-center gap-6 pt-4 border-t border-surface-700/50">
              <div className="flex-1 text-center">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-surface-500 mb-1.5">Win Rate</p>
                <p className="text-2xl font-black">{userStats.winRate}%</p>
              </div>
              <div className="h-10 w-px bg-gradient-to-b from-transparent via-surface-700 to-transparent" />
              <div className="flex-1 text-center">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-surface-500 mb-1.5">Games</p>
                <p className="text-2xl font-black">{userStats.games}</p>
              </div>
              <div className="h-10 w-px bg-gradient-to-b from-transparent via-surface-700 to-transparent" />
              <div className="flex-1 text-center">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-surface-500 mb-1.5">Streak</p>
                <p className="text-2xl font-black text-brand-400">+{userStats.streak}</p>
              </div>
            </div>
          </div>

          {/* Rating History */}
          <div className="p-6 rounded-2xl bg-surface-800/50 border border-surface-700/50">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-semibold">Rating History</h3>
              <span className="text-surface-400 text-sm">14 days</span>
            </div>
            <RatingChart data={ratingHistory} height={180} />
          </div>

          {/* Win/Loss Chart */}
          <div className="p-6 rounded-2xl bg-surface-800/50 border border-surface-700/50">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-semibold">Win/Loss</h3>
              <span className="text-surface-400 text-sm">7 days</span>
            </div>
            <WinLossChart data={winLossData} />
          </div>

          {/* Season Stats */}
          <div className="p-6 rounded-2xl bg-surface-800/50 border border-surface-700/50">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-semibold">Season Stats</h3>
              <span className="text-surface-400 text-sm uppercase tracking-wide">Season 3</span>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="p-4 rounded-xl bg-gradient-to-br from-surface-700/40 to-surface-800/20 text-center">
                <div className="text-surface-400 text-xs uppercase tracking-wide mb-1">Peak</div>
                <div className="text-2xl font-display font-bold">{userStats.peakElo}</div>
                <div className="text-surface-500 text-xs">ELO</div>
              </div>
              <div className="p-4 rounded-xl bg-gradient-to-br from-brand-500/15 to-brand-500/5 border border-brand-500/20 text-center">
                <div className="text-surface-400 text-xs uppercase tracking-wide mb-1">Wins</div>
                <div className="text-2xl font-display font-bold text-brand-400">{userStats.wins}</div>
                <div className="text-surface-500 text-xs">Games</div>
              </div>
              <div className="p-4 rounded-xl bg-gradient-to-br from-red-500/15 to-red-500/5 border border-red-500/20 text-center">
                <div className="text-surface-400 text-xs uppercase tracking-wide mb-1">Losses</div>
                <div className="text-2xl font-display font-bold text-red-400">{userStats.losses}</div>
                <div className="text-surface-500 text-xs">Games</div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Leaderboard */}
        <Leaderboard players={leaderboard} currentUser={currentUser} />
      </div>
    </div>
  );
}

function Leaderboard({ players, currentUser }: { players: Player[]; currentUser: Player }) {
  // 'above' = user scrolled past, pin to top; 'below' = not reached yet, pin to bottom; 'visible' = in view
  const [userPosition, setUserPosition] = useState<'above' | 'visible' | 'below'>('below');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const userRowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    const userRow = userRowRef.current;
    if (!scrollContainer || !userRow) return;

    const checkPosition = () => {
      const containerRect = scrollContainer.getBoundingClientRect();
      const rowRect = userRow.getBoundingClientRect();

      if (rowRect.bottom < containerRect.top) {
        setUserPosition('above');
      } else if (rowRect.top > containerRect.bottom) {
        setUserPosition('below');
      } else {
        setUserPosition('visible');
      }
    };

    scrollContainer.addEventListener('scroll', checkPosition);
    checkPosition(); // Initial check

    return () => scrollContainer.removeEventListener('scroll', checkPosition);
  }, []);

  // Find if current user is in the main list
  const userInList = players.find(p => p.rank === currentUser.rank);

  return (
    <div className="rounded-2xl bg-surface-800 border border-surface-700/50 overflow-hidden flex flex-col max-h-[600px]">
      {/* Pinned to top when scrolled past */}
      {userPosition === 'above' && (
        <div className="shrink-0 border-b-2 border-surface-600">
          <LeaderboardRow {...currentUser} />
        </div>
      )}

      {/* Leaderboard Header */}
      <div className="grid grid-cols-[60px_1fr_auto] gap-4 px-6 py-3 border-b border-surface-700/50 text-xs text-surface-400 uppercase tracking-wider font-semibold shrink-0">
        <div className="text-center">Rank</div>
        <div>Player</div>
        <div className="text-right">Rank/Rating</div>
      </div>

      {/* Leaderboard Rows */}
      <div ref={scrollContainerRef} className="divide-y divide-surface-700/30 overflow-y-auto flex-1">
        {players.map((player) => (
          <div key={player.rank} ref={player.rank === currentUser.rank ? userRowRef : undefined}>
            <LeaderboardRow {...player} isYou={player.rank === currentUser.rank} />
          </div>
        ))}
        {/* If user is not in main list, show them at their position */}
        {!userInList && (
          <div ref={userRowRef}>
            <LeaderboardRow {...currentUser} />
          </div>
        )}
      </div>

      {/* Pinned to bottom when not yet reached */}
      {userPosition === 'below' && (
        <div className="shrink-0 border-t-2 border-surface-600">
          <LeaderboardRow {...currentUser} />
        </div>
      )}
    </div>
  );
}

function LeaderboardRow({ rank, name, initials, elo, rankTier, level, isYou }: Player) {
  return (
    <div className={`grid grid-cols-[60px_1fr_auto] gap-4 px-6 h-16 items-center ${isYou ? 'bg-brand-500/10' : 'hover:bg-surface-700/30'} transition-colors`}>
      <div className="text-center text-sm">
        <span className="text-surface-500 font-medium">#</span>
        <span className={`font-bold ${isYou ? 'text-brand-400' : 'text-white'}`}>{rank}</span>
      </div>
      <div className="flex items-center gap-2 min-w-0">
        <Avatar initials={initials} size="sm" bordered={false} />
        <span className={`font-medium text-sm truncate ${isYou ? 'text-brand-400' : 'text-white'}`}>
          {name}
          {isYou && <span className="text-xs text-surface-400 ml-1.5">(You)</span>}
        </span>
      </div>
      <div className="flex items-center gap-2 justify-end">
        <RankIcon rank={rankTier} level={level} size="md" />
        <span className={`font-bold tabular-nums text-base ${isYou ? 'text-brand-400' : 'text-white'}`}>
          {elo}
        </span>
      </div>
    </div>
  );
}

function WinLossChart({ data }: { data: typeof winLossData }) {
  const maxValue = Math.max(...data.flatMap(d => [d.wins, d.losses]));
  const scale = maxValue > 0 ? 100 / maxValue : 1;

  return (
    <div className="space-y-2">
      {/* Y-axis labels and chart */}
      <div className="flex gap-4">
        {/* Y-axis */}
        <div className="flex flex-col justify-between text-xs text-surface-500 w-6 text-right">
          <span>{maxValue}</span>
          <span>{Math.round(maxValue / 2)}</span>
          <span>0</span>
        </div>

        {/* Bars */}
        <div className="flex-1 flex items-end justify-around gap-2 h-32">
          {data.map((day, i) => (
            <div key={i} className="flex-1 flex flex-col items-center gap-1">
              <div className="w-full flex gap-0.5 items-end h-24">
                {/* Wins bar */}
                <div
                  className="flex-1 bg-brand-500 rounded-t"
                  style={{ height: `${day.wins * scale}%` }}
                />
                {/* Losses bar */}
                <div
                  className="flex-1 bg-red-500 rounded-t"
                  style={{ height: `${day.losses * scale}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* X-axis labels */}
      <div className="flex gap-4">
        <div className="w-6" /> {/* Spacer for Y-axis */}
        <div className="flex-1 flex justify-around">
          {data.map((day, i) => (
            <span key={i} className="text-xs text-surface-500">{day.date}</span>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-4 mt-2">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 bg-brand-500 rounded" />
          <span className="text-xs text-surface-400">Wins</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 bg-red-500 rounded" />
          <span className="text-xs text-surface-400">Losses</span>
        </div>
      </div>
    </div>
  );
}
